import Phaser from 'phaser';
import { MMOStateManager } from '../../managers/MMOStateManager';
import { EventBus } from '../../utils/EventBus';

/**
 * MobileDOMOverlay — Full-featured DOM-based HUD shell for mobile.
 *
 * Contains:
 *   • Top bar: Credits + Hamburger menu + quick camera/wallet actions
 *   • Bottom action rail: FLEET, STATS, CHAT, RADAR
 *   • Fleet panel (slide-up sheet): ships + stations list
 *   • Stats panel (slide-up sheet): empire summary
 *   • Nav drawer (slide-in from left): all 22 nav items grouped by category
 *
 * All panels respect safe-area insets and cooperate with the Phaser
 * CommandDock via EventBus events (`mmo-command-dock-shown/hidden`)
 * to avoid bottom-of-screen collisions.
 */
export interface MobileDOMOverlayOptions {
  onBuyShip: () => void;
  onToggleChat: () => void;
  onToggleRadar: () => void;
  onNavAction: (key: NavKey) => void;
}

export type NavKey =
  // You
  | 'profile' | 'how_to'
  // Commerce
  | 'shop' | 'buy_isd' | 'trade' | 'parts'
  // Operations
  | 'ore' | 'crew' | 'station' | 'wreck' | 'insure'
  // Research
  | 'research' | 'tech'
  // Alliance
  | 'alliance_hq' | 'alliance' | 'browse_alliances' | 'leaderboard' | 'dm'
  // Camera
  | 'center' | 'follow'
  // Wallet
  | 'refund' | 'withdraw';

interface NavItem {
  key: NavKey;
  label: string;
  icon: string;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    title: 'You',
    items: [
      { key: 'profile', label: 'Profile',      icon: '👤' },
      { key: 'how_to',  label: 'How To Play',  icon: '❓' },
    ],
  },
  {
    title: 'Commerce',
    items: [
      { key: 'shop',    label: 'Ship Store',   icon: '🚀' },
      { key: 'buy_isd', label: 'Buy $ISD',     icon: '💳' },
      { key: 'trade',   label: 'Trade Hub',    icon: '🏛' },
      { key: 'parts',   label: 'Components',   icon: '🔧' },
    ],
  },
  {
    title: 'Operations',
    items: [
      { key: 'ore',     label: 'Minerals',     icon: '⛏' },
      { key: 'crew',    label: 'Crew',         icon: '👥' },
      { key: 'station', label: 'Stations',     icon: '🛰' },
      { key: 'wreck',   label: 'Wreckage',     icon: '💥' },
      { key: 'insure',  label: 'Insurance',    icon: '🛡' },
    ],
  },
  {
    title: 'Research',
    items: [
      { key: 'research', label: 'Relics',      icon: '🔬' },
      { key: 'tech',     label: 'Tech Tree',   icon: '🧬' },
    ],
  },
  {
    title: 'Alliance',
    items: [
      { key: 'alliance_hq',      label: 'Alliance HQ',  icon: '🏛' },
      { key: 'alliance',         label: 'Alliance',     icon: '⚔' },
      { key: 'browse_alliances', label: 'Browse',       icon: '🔍' },
      { key: 'leaderboard',      label: 'Leaderboard',  icon: '🏆' },
      { key: 'dm',               label: 'Messages',     icon: '✉' },
    ],
  },
  {
    title: 'Camera',
    items: [
      { key: 'center', label: 'Center Ship',   icon: '🎯' },
      { key: 'follow', label: 'Toggle Follow', icon: '📍' },
    ],
  },
  {
    title: 'Wallet',
    items: [
      { key: 'refund',   label: 'Refund',    icon: '💰' },
      { key: 'withdraw', label: 'Withdraw',  icon: '💵' },
    ],
  },
];

export class MobileDOMOverlay {
  private domElement: Phaser.GameObjects.DOMElement;
  private mmoState: MMOStateManager;
  private root: HTMLDivElement;
  private fleetOpen = false;
  private statsOpen = false;
  private chatUnread = 0;

  // Stored handlers for cleanup
  private onTickBound = (): void => this.updateStats();
  private onChatMessageBound = (): void => this.incrementChatUnread();
  // The dock lift hooks remain only as bind-points so the listeners below have
  // stable cleanup references. The current mobile dock is left-anchored, so it
  // does not cover the bottom action rail.
  private onDockShownBound = (): void => { /* intentionally no-op: dock is left-side */ };
  private onDockHiddenBound = (): void => { /* intentionally no-op */ };
  private onFollowChangedBound = (data: { enabled: boolean } | undefined): void => {
    const btn = this.root.querySelector<HTMLElement>('#btn-top-follow');
    if (!btn) return;
    btn.classList.toggle('active', !!data?.enabled);
  };

  constructor(
    scene: Phaser.Scene,
    mmoState: MMOStateManager,
    opts: MobileDOMOverlayOptions,
  ) {
    this.mmoState = mmoState;

    const div = document.createElement('div');
    div.style.width = '100%';
    div.style.height = '100%';
    div.style.pointerEvents = 'none';
    // Marker class so the CSS rule below can target this element specifically
    // and force pointer-events:none with !important. Phaser's DOMElement
    // pipeline rewrites the inline style to `pointer-events: auto`, so the
    // inline value above is NOT enough — without the !important rule below,
    // this 100%-sized wrapper would intercept every tap on transparent
    // regions, breaking ship selection, drag-pan, and pinch-zoom.
    div.classList.add('mobile-hud-host');
    this.root = div;

    div.innerHTML = `
      <style>
        .mobile-hud {
          font-family: "Orbitron", system-ui, sans-serif;
          position: absolute; inset: 0; width: 100vw;
          /* iOS Safari/Chrome's bottom toolbar (back/forward/+/tabs/...)
             sits ON TOP of the layout viewport. 100vh INCLUDES that hidden
             strip, so any fixed/absolute "bottom: 0" element gets clipped
             behind the toolbar. 100dvh = dynamic viewport height, which
             tracks the *visible* area as the toolbar shows/hides. Falls
             back to 100vh on older browsers (Safari < 15.4 / Chrome < 108)
             via the supports cascade below. */
          height: 100vh;
          height: 100dvh;
          pointer-events: none; z-index: 999;
        }

        /* Phasers DOMElement pipeline forcibly sets pointer-events:auto on
           the host node we hand to scene.add.dom(). That host fills the
           whole viewport, so it intercepts every tap on transparent regions
           — breaking ship selection, drag-pan, and pinch-zoom. The inline
           pointer-events:none set in the constructor is clobbered by
           Phaser. The only reliable override is CSS with !important. */
        .mobile-hud-host {
          pointer-events: none !important;
        }

        /* Only visible / functional controls should intercept taps.
           A blanket pointer-events:auto on every descendant makes every
           wrapper div (including transparent flex containers) capture
           clicks — which manifests as "taps near edges dont register"
           because the HUD action rail, top bar, and nav drawer wrappers
           all sit along the screen edges. Taps must pass through any
           transparent region of the HUD to reach the Phaser canvas. */
        .mobile-hud .m-top-bar,
        .mobile-hud .m-top-btn-menu,
        .mobile-hud .m-top-quick-btn,
        .mobile-hud .m-isd-pill,
        .mobile-hud .m-fab,
        .mobile-hud .m-backdrop.open,
        .mobile-hud .m-sheet.open,
        .mobile-hud .m-sheet-close,
        .mobile-hud .m-fleet-tab,
        .mobile-hud .m-fleet-row,
        .mobile-hud .m-nav-drawer.open,
        .mobile-hud .m-nav-close,
        .mobile-hud .m-nav-item { pointer-events: auto; }
        /* Force transparent wrappers to remain click-through. */
        .mobile-hud .m-action-rail,
        .mobile-hud .m-fab-container,
        .mobile-hud .m-fab-container-left,
        .mobile-hud .m-nav-drawer,
        .mobile-hud .m-sheet,
        .mobile-hud .m-backdrop { pointer-events: none; }

        /* ──────────── Top bar (44px lean) ──────────── */
        .m-top-bar {
          position: absolute;
          top: calc(env(safe-area-inset-top, 0px) + 6px);
          left: 8px; right: 8px;
          height: 44px;
          background: rgba(15, 20, 30, 0.82);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          border: 1px solid rgba(100, 200, 255, 0.22);
          border-radius: 22px;
          display: flex;
          align-items: center;
          padding: 0 6px;
          box-shadow: 0 6px 18px rgba(0, 0, 0, 0.55);
          justify-content: space-between;
          gap: 6px;
        }

        .m-top-btn-menu {
          width: 36px; height: 36px;
          border-radius: 50%;
          border: 1px solid rgba(0, 255, 255, 0.45);
          background: rgba(0, 255, 255, 0.08);
          color: #00ffff;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          padding: 0;
          flex-shrink: 0;
        }
        .m-top-btn-menu:active { transform: scale(0.92); }
        .m-top-btn-menu svg { width: 18px; height: 18px; stroke: currentColor; fill: none; stroke-width: 2.5; }

        /* Compact icon-only quick-action buttons next to hamburger.
           These mirror the desktop top-bar buttons (CENTER, FOLLOW, WITHDRAW)
           so single-tap access matches the desktop affordance. */
        .m-top-quick-row {
          display: flex;
          align-items: center;
          gap: 4px;
          flex-shrink: 0;
        }
        .m-top-quick-btn {
          width: 34px; height: 34px;
          border-radius: 50%;
          border: 1px solid rgba(100, 200, 255, 0.35);
          background: rgba(15, 20, 30, 0.55);
          color: #9ad9ff;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer;
          padding: 0;
          font-size: 1rem;
          line-height: 1;
          flex-shrink: 0;
          transition: transform 120ms ease, box-shadow 120ms ease, background 120ms ease;
        }
        .m-top-quick-btn:active { transform: scale(0.9); }
        .m-top-quick-btn.accent-amber {
          border-color: rgba(255, 170, 51, 0.55);
          color: #ffaa33;
          background: rgba(255, 170, 51, 0.08);
        }
        .m-top-quick-btn.accent-green {
          border-color: rgba(0, 255, 136, 0.55);
          color: #00ff88;
          background: rgba(0, 255, 136, 0.08);
        }
        .m-top-quick-btn.active {
          background: rgba(0, 255, 255, 0.22);
          color: #00ffff;
          border-color: #00ffff;
          box-shadow: 0 0 10px rgba(0, 255, 255, 0.55);
        }

        .m-isd-pill {
          display: flex;
          align-items: center;
          gap: 6px;
          height: 32px;
          padding: 0 12px;
          background: rgba(0, 255, 255, 0.1);
          border: 1px solid rgba(0, 255, 255, 0.35);
          border-radius: 16px;
          color: #00ffff;
          font-family: "Orbitron", sans-serif;
          font-size: 0.82rem;
          font-weight: 900;
          letter-spacing: 0.5px;
          text-shadow: 0 0 8px rgba(0, 255, 255, 0.5);
          flex-shrink: 0;
        }
        .m-isd-pill-label {
          font-size: 0.55rem;
          color: #8899BB;
          letter-spacing: 1px;
          font-weight: 700;
          text-transform: uppercase;
          text-shadow: none;
        }

        .m-top-tick {
          flex: 1;
          text-align: center;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-family: "Orbitron", sans-serif;
          font-size: 0.62rem;
          color: #6677AA;
          letter-spacing: 1px;
          min-width: 0;
        }
        /* Hide turn indicator on very narrow screens so quick-action buttons
           always have room for hit-targets. The tick still appears in the
           Empire Stats sheet, so the data isn't lost — just deprioritized. */
        @media (max-width: 420px) {
          .m-top-tick { display: none; }
        }

        /* ──────────── Bottom action rail ──────────── */
        .m-action-rail {
          position: absolute;
          left: 8px;
          right: 8px;
          bottom: calc(env(safe-area-inset-bottom, 0px) + 10px);
          min-height: 68px;
          padding: 8px;
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          align-items: center;
          gap: 10px;
          border: 1px solid rgba(100, 200, 255, 0.22);
          border-radius: 18px;
          background: rgba(8, 12, 25, 0.78);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.55), inset 0 0 18px rgba(0, 255, 255, 0.05);
          box-sizing: border-box;
          transition: transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .m-action-rail.lifted { transform: translateY(-120px); }

        .m-fab-container, .m-fab-container-left {
          display: flex;
          flex-direction: row;
          align-items: center;
          gap: 6px;
          transition: transform 220ms cubic-bezier(0.34, 1.56, 0.64, 1);
        }
        .m-fab-container       { justify-content: flex-end; }
        .m-fab-container-left  { justify-content: flex-start; }
        /* Lift = mobile dock height (110px) + 10px gap */
        .m-fab-container.lifted       { transform: translateY(-120px); }
        .m-fab-container-left.lifted  { transform: translateY(-120px); }

        .m-fab {
          width: clamp(50px, 15vw, 56px); height: 52px;
          border-radius: 14px;
          background: rgba(15, 20, 30, 0.82);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid #00ffff;
          color: #00ffff;
          display: flex; align-items: center; justify-content: center; flex-direction: column;
          cursor: pointer;
          position: relative;
          box-shadow: inset 0 0 12px rgba(0, 255, 255, 0.08), 0 0 18px rgba(0, 255, 255, 0.25);
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }
        .m-fab:active { transform: scale(0.9); box-shadow: 0 0 10px rgba(0, 255, 255, 0.5); }
        .m-fab-icon   { font-size: 1.05rem; margin-bottom: 2px; line-height: 1; }
        .m-fab-label  { font-size: 0.44rem; font-weight: 800; letter-spacing: 0.8px; line-height: 1; }

        .m-fab.accent-green { border-color: #00ff88; color: #00ff88;
          box-shadow: inset 0 0 15px rgba(0,255,136,0.1), 0 0 25px rgba(0,255,136,0.3); }
        .m-fab.accent-amber { border-color: #ffaa33; color: #ffaa33;
          box-shadow: inset 0 0 15px rgba(255,170,51,0.1), 0 0 25px rgba(255,170,51,0.3); }
        .m-fab.accent-magenta { border-color: #ff66ff; color: #ff66ff;
          box-shadow: inset 0 0 15px rgba(255,102,255,0.1), 0 0 25px rgba(255,102,255,0.3); }

        .m-fab-badge {
          position: absolute;
          top: -4px;
          right: -4px;
          min-width: 20px;
          height: 20px;
          padding: 0 6px;
          box-sizing: border-box;
          border-radius: 10px;
          background: #ff4444;
          color: #ffffff;
          font-size: 0.65rem;
          font-weight: 900;
          line-height: 20px;
          text-align: center;
          border: 2px solid rgba(15, 20, 30, 0.95);
          box-shadow: 0 0 6px rgba(255, 68, 68, 0.8);
          pointer-events: none;
          display: none;
        }
        .m-fab-badge.visible { display: block; }

        /* ──────────── Backdrop (shared by sheets + drawer) ──────────── */
        .m-backdrop {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.55);
          backdrop-filter: blur(3px);
          -webkit-backdrop-filter: blur(3px);
          opacity: 0;
          pointer-events: none;
          transition: opacity 200ms ease;
          z-index: 998;
        }
        .m-backdrop.open { opacity: 1; pointer-events: auto; }

        /* ──────────── Slide-up sheets (fleet, stats) ──────────── */
        .m-sheet {
          position: absolute;
          left: 8px; right: 8px;
          bottom: calc(env(safe-area-inset-bottom, 16px) + 8px);
          /* dvh keeps the sheet's max-height anchored to the *visible*
             viewport — without it iOS Chrome's bottom toolbar (~50-65px)
             eats the lower portion of the sheet's content, hiding the
             ship list / X-close. */
          max-height: calc(100vh - 80px);
          max-height: calc(100dvh - 80px);
          background: linear-gradient(180deg, rgba(10,15,30,0.97) 0%, rgba(20,25,50,0.97) 100%);
          border: 1px solid rgba(0, 255, 255, 0.35);
          border-radius: 18px;
          box-shadow: 0 12px 48px rgba(0,0,0,0.7), 0 0 24px rgba(0,255,255,0.15);
          padding: 16px 14px 12px;
          transform: translateY(calc(100% + 32px));
          transition: transform 260ms cubic-bezier(0.34, 1.56, 0.64, 1);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          z-index: 999;
        }
        .m-sheet.open { transform: translateY(0); }

        .m-sheet-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
        }
        .m-sheet-title {
          font-family: "Orbitron", sans-serif;
          font-size: 0.85rem;
          font-weight: 900;
          letter-spacing: 2px;
          color: #00ffff;
          text-transform: uppercase;
          text-shadow: 0 0 8px rgba(0,255,255,0.5);
        }
        .m-sheet-close {
          width: 32px; height: 32px;
          border-radius: 50%;
          border: 1px solid rgba(0,255,255,0.25);
          background: rgba(0,0,0,0.3);
          color: #00ffff;
          font-size: 1rem;
          cursor: pointer;
        }

        /* ──────────── Stats grid (fleet + stats sheets) ──────────── */
        .m-stats-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 8px;
          margin-bottom: 10px;
          padding: 10px;
          border: 1px solid rgba(0,255,255,0.15);
          border-radius: 8px;
          background: rgba(0,0,0,0.25);
        }
        .m-stats-grid.four-col { grid-template-columns: repeat(4, 1fr); }
        .m-stat-cell {
          text-align: center;
          padding: 6px 4px;
          border-radius: 6px;
          background: rgba(0,255,255,0.04);
          border: 1px solid rgba(0,255,255,0.08);
        }
        .m-stat-cell-label { font-size: 0.5rem; color: #8899BB; letter-spacing: 1px; text-transform: uppercase; }
        .m-stat-cell-val   { font-size: 1.05rem; color: #E0E8FF; font-weight: 900; margin-top: 2px; }
        .m-stat-cell-val.isd { color: #00ffff; }

        /* ──────────── Fleet tabs + list ──────────── */
        .m-fleet-tabs {
          display: flex;
          gap: 4px;
          margin-bottom: 6px;
        }
        .m-fleet-tab {
          flex: 1;
          padding: 8px;
          font-family: "Orbitron", sans-serif;
          font-size: 0.7rem;
          font-weight: 800;
          letter-spacing: 1.5px;
          text-transform: uppercase;
          color: #667799;
          border: 1px solid rgba(0,255,255,0.1);
          border-radius: 6px;
          background: rgba(0,0,0,0.2);
          cursor: pointer;
        }
        .m-fleet-tab.active {
          color: #00ffff;
          border-color: rgba(0,255,255,0.45);
          background: rgba(0,255,255,0.08);
        }

        .m-fleet-list {
          flex: 1;
          overflow-y: auto;
          overscroll-behavior: contain;
          -webkit-overflow-scrolling: touch;
          padding: 2px;
          min-height: 120px;
        }
        .m-fleet-list::-webkit-scrollbar { width: 4px; }
        .m-fleet-list::-webkit-scrollbar-thumb { background: rgba(0,255,255,0.25); border-radius: 2px; }

        .m-fleet-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 10px;
          margin-bottom: 4px;
          border: 1px solid rgba(0,255,255,0.08);
          border-radius: 6px;
          background: rgba(0,255,255,0.02);
          cursor: pointer;
          transition: all 0.12s;
        }
        .m-fleet-row:active {
          transform: scale(0.98);
          background: rgba(0,255,255,0.12);
          border-color: rgba(0,255,255,0.4);
        }
        .m-fleet-row-left { display: flex; flex-direction: column; line-height: 1.2; }
        .m-fleet-row-name { font-size: 0.78rem; color: #E0E8FF; font-weight: 700; }
        .m-fleet-row-sub  { font-size: 0.6rem; color: #8899BB; margin-top: 2px; letter-spacing: 0.5px; }
        .m-fleet-row-hp   { font-size: 0.65rem; color: #00ff88; font-weight: 800; }
        .m-fleet-row-hp.low { color: #ff6644; }
        .m-fleet-empty {
          text-align: center;
          color: #667799;
          font-size: 0.75rem;
          padding: 30px 10px;
        }

        /* ──────────── Nav drawer ──────────── */
        .m-nav-drawer {
          position: absolute;
          top: 0; bottom: 0;
          left: 0;
          width: min(85vw, 360px);
          background: linear-gradient(180deg, rgba(8,12,25,0.98) 0%, rgba(16,22,45,0.98) 100%);
          border-right: 1px solid rgba(0, 255, 255, 0.35);
          box-shadow: 6px 0 32px rgba(0,0,0,0.65), 0 0 24px rgba(0,255,255,0.12);
          transform: translateX(-105%);
          transition: transform 260ms cubic-bezier(0.34, 1.56, 0.64, 1);
          display: flex;
          flex-direction: column;
          z-index: 1000;
          padding-top: calc(env(safe-area-inset-top, 12px) + 12px);
        }
        .m-nav-drawer.open { transform: translateX(0); }

        .m-nav-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 6px 16px 12px;
          border-bottom: 1px solid rgba(0, 255, 255, 0.12);
        }
        .m-nav-title {
          font-family: "Orbitron", sans-serif;
          font-size: 0.95rem;
          font-weight: 900;
          letter-spacing: 2px;
          color: #00ffff;
          text-transform: uppercase;
          text-shadow: 0 0 8px rgba(0,255,255,0.5);
        }
        .m-nav-close {
          width: 34px; height: 34px;
          border-radius: 50%;
          border: 1px solid rgba(0,255,255,0.25);
          background: rgba(0,0,0,0.3);
          color: #00ffff;
          font-size: 1rem;
          cursor: pointer;
        }

        .m-nav-list {
          flex: 1;
          overflow-y: auto;
          overscroll-behavior: contain;
          -webkit-overflow-scrolling: touch;
          padding: 12px 10px calc(env(safe-area-inset-bottom, 16px) + 20px);
        }
        .m-nav-list::-webkit-scrollbar { width: 4px; }
        .m-nav-list::-webkit-scrollbar-thumb { background: rgba(0,255,255,0.25); border-radius: 2px; }

        .m-nav-group { margin-bottom: 14px; }
        .m-nav-group-title {
          font-family: "Orbitron", sans-serif;
          font-size: 0.6rem;
          color: #6688AA;
          letter-spacing: 2.5px;
          text-transform: uppercase;
          padding: 4px 10px 6px;
        }
        .m-nav-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 6px;
        }
        .m-nav-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 8px;
          border: 1px solid rgba(0, 255, 255, 0.12);
          border-radius: 8px;
          background: rgba(0, 255, 255, 0.04);
          color: #CCDDEE;
          font-family: "Orbitron", sans-serif;
          font-size: 0.68rem;
          font-weight: 700;
          letter-spacing: 0.5px;
          cursor: pointer;
          text-align: left;
          transition: all 0.12s;
        }
        .m-nav-item:active {
          transform: scale(0.97);
          background: rgba(0, 255, 255, 0.15);
          border-color: rgba(0, 255, 255, 0.45);
          color: #ffffff;
        }
        .m-nav-item-icon {
          font-size: 1.1rem;
          line-height: 1;
          width: 22px;
          text-align: center;
          flex-shrink: 0;
        }
      </style>

      <div class="mobile-hud">
        <!-- Top bar: hamburger + quick actions + turn + $ISD pill -->
        <div class="m-top-bar">
          <button class="m-top-btn-menu" id="btn-nav-menu" aria-label="Menu">
            <svg viewBox="0 0 24 24">
              <line x1="4" y1="7" x2="20" y2="7"/>
              <line x1="4" y1="12" x2="20" y2="12"/>
              <line x1="4" y1="17" x2="20" y2="17"/>
            </svg>
          </button>
          <!-- Quick actions mirror the desktop top-bar buttons so they're
               one-tap accessible instead of buried inside the nav drawer. -->
          <div class="m-top-quick-row">
            <button class="m-top-quick-btn" id="btn-top-center" aria-label="Center on ship" title="Center on ship">&#127919;</button>
            <button class="m-top-quick-btn accent-green" id="btn-top-follow" aria-label="Toggle follow" title="Toggle camera follow">&#128205;</button>
            <button class="m-top-quick-btn accent-amber" id="btn-top-withdraw" aria-label="Withdraw $ISD" title="Withdraw">&#128181;</button>
          </div>
          <div class="m-top-tick" id="m-tick">Turn 0</div>
          <div class="m-isd-pill">
            <span class="m-isd-pill-label">$ISD</span>
            <span id="m-isd">0</span>
          </div>
        </div>

        <!-- Bottom action rail -->
        <div class="m-action-rail" id="fab-rail">
          <div class="m-fab-container-left" id="fab-left">
            <div class="m-fab accent-green" id="btn-fleet" aria-label="Fleet">
              <span class="m-fab-icon">&#128755;</span>
              <span class="m-fab-label">FLEET</span>
              <span class="m-fab-badge" id="fleet-badge">0</span>
            </div>
            <div class="m-fab accent-amber" id="btn-stats" aria-label="Empire Stats">
              <span class="m-fab-icon">&#128200;</span>
              <span class="m-fab-label">STATS</span>
            </div>
          </div>

          <div class="m-fab-container" id="fab-right">
            <div class="m-fab" id="btn-comm" aria-label="Chat">
              <span class="m-fab-icon">&#128172;</span>
              <span class="m-fab-label">CHAT</span>
              <span class="m-fab-badge" id="chat-badge">0</span>
            </div>
            <div class="m-fab accent-magenta" id="btn-radar" aria-label="Radar">
              <span class="m-fab-icon">&#128225;</span>
              <span class="m-fab-label">RADAR</span>
            </div>
          </div>
        </div>

        <!-- Shared backdrop for all overlays -->
        <div class="m-backdrop" id="m-backdrop"></div>

        <!-- Nav drawer (slide from left) -->
        <div class="m-nav-drawer" id="nav-drawer">
          <div class="m-nav-header">
            <span class="m-nav-title">Command Center</span>
            <button class="m-nav-close" id="nav-close">&#x2715;</button>
          </div>
          <div class="m-nav-list" id="nav-list">
            ${renderNavGroups()}
          </div>
        </div>

        <!-- Fleet sheet (slide-up) -->
        <div class="m-sheet" id="fleet-sheet">
          <div class="m-sheet-header">
            <span class="m-sheet-title">Fleet &amp; Assets</span>
            <button class="m-sheet-close" id="fleet-close">&#x2715;</button>
          </div>
          <div class="m-stats-grid four-col">
            <div class="m-stat-cell">
              <div class="m-stat-cell-label">Stations</div>
              <div class="m-stat-cell-val" id="f-stations">0</div>
            </div>
            <div class="m-stat-cell">
              <div class="m-stat-cell-label">Ships</div>
              <div class="m-stat-cell-val" id="f-ships">0</div>
            </div>
            <div class="m-stat-cell">
              <div class="m-stat-cell-label">Explored</div>
              <div class="m-stat-cell-val" id="f-explored">0</div>
            </div>
            <div class="m-stat-cell">
              <div class="m-stat-cell-label">$ISD</div>
              <div class="m-stat-cell-val isd" id="f-isd">0</div>
            </div>
          </div>
          <div class="m-fleet-tabs">
            <button class="m-fleet-tab active" data-tab="ships">Ships</button>
            <button class="m-fleet-tab" data-tab="stations">Stations</button>
          </div>
          <div class="m-fleet-list" id="fleet-list"></div>
        </div>

        <!-- Stats sheet (slide-up) -->
        <div class="m-sheet" id="stats-sheet">
          <div class="m-sheet-header">
            <span class="m-sheet-title">Empire Stats</span>
            <button class="m-sheet-close" id="stats-close">&#x2715;</button>
          </div>
          <div class="m-stats-grid">
            <div class="m-stat-cell">
              <div class="m-stat-cell-label">ISD</div>
              <div class="m-stat-cell-val isd" id="s-isd">0</div>
            </div>
            <div class="m-stat-cell">
              <div class="m-stat-cell-label">Ships</div>
              <div class="m-stat-cell-val" id="s-ships">0</div>
            </div>
            <div class="m-stat-cell">
              <div class="m-stat-cell-label">Stations</div>
              <div class="m-stat-cell-val" id="s-stations">0</div>
            </div>
            <div class="m-stat-cell">
              <div class="m-stat-cell-label">Planets</div>
              <div class="m-stat-cell-val" id="s-planets">0</div>
            </div>
            <div class="m-stat-cell">
              <div class="m-stat-cell-label">Explored</div>
              <div class="m-stat-cell-val" id="s-explored">0</div>
            </div>
            <div class="m-stat-cell">
              <div class="m-stat-cell-label">Tick</div>
              <div class="m-stat-cell-val" id="s-tick">0</div>
            </div>
            <div class="m-stat-cell">
              <div class="m-stat-cell-label">Alliance</div>
              <div class="m-stat-cell-val" id="s-alliance" style="font-size: 0.8rem;">—</div>
            </div>
            <div class="m-stat-cell">
              <div class="m-stat-cell-label">Territories</div>
              <div class="m-stat-cell-val" id="s-territories">0</div>
            </div>
          </div>
        </div>
      </div>
    `;

    this.domElement = scene.add.dom(0, 0, div).setOrigin(0, 0);

    // Initial binding
    this.updateStats();

    // Live update hook
    EventBus.on('mmo-tick-update', this.onTickBound);
    EventBus.on('mmo-chat-message', this.onChatMessageBound);
    EventBus.on('chat-message', this.onChatMessageBound);
    EventBus.on('mmo-command-dock-shown', this.onDockShownBound);
    EventBus.on('mmo-command-dock-hidden', this.onDockHiddenBound);

    // Top bar buttons — BUY SHIP lives in the nav drawer now; top bar is lean.
    div.querySelector('#btn-nav-menu')?.addEventListener('click', () => this.openNav());

    // Top-bar quick actions: route through the same handler chain as the
    // nav drawer items so behavior stays in sync.
    div.querySelector('#btn-top-center')?.addEventListener('click', () => {
      opts.onNavAction('center');
    });
    div.querySelector('#btn-top-follow')?.addEventListener('click', () => {
      // Toggle visual active state. The actual follow state is owned by
      // MMOGameScene, which broadcasts 'camera-follow-changed' below.
      opts.onNavAction('follow');
    });
    div.querySelector('#btn-top-withdraw')?.addEventListener('click', () => {
      opts.onNavAction('withdraw');
    });

    // Reflect camera-follow on/off state on the FOLLOW quick button so the
    // user can see whether follow mode is currently engaged.
    EventBus.on('camera-follow-changed', this.onFollowChangedBound);

    // FABs: Right
    div.querySelector('#btn-comm')?.addEventListener('click', () => {
      this.resetChatUnread();
      opts.onToggleChat();
    });
    div.querySelector('#btn-radar')?.addEventListener('click', () => opts.onToggleRadar());

    // FABs: Left
    div.querySelector('#btn-fleet')?.addEventListener('click', () => this.openFleet());
    div.querySelector('#btn-stats')?.addEventListener('click', () => this.openStats());

    // Sheet close buttons
    div.querySelector('#fleet-close')?.addEventListener('click', () => this.closeAll());
    div.querySelector('#stats-close')?.addEventListener('click', () => this.closeAll());
    div.querySelector('#nav-close')?.addEventListener('click', () => this.closeAll());
    div.querySelector('#m-backdrop')?.addEventListener('click', () => this.closeAll());

    // Fleet tabs
    div.querySelectorAll<HTMLElement>('.m-fleet-tab').forEach((tab) => {
      tab.addEventListener('click', () => {
        div.querySelectorAll<HTMLElement>('.m-fleet-tab').forEach((t) => t.classList.remove('active'));
        tab.classList.add('active');
        const tabName = (tab.dataset.tab ?? 'ships');
        this.renderFleetList(tabName);
      });
    });

    // Nav drawer items — dispatch to onNavAction and close drawer
    div.querySelectorAll<HTMLElement>('.m-nav-item').forEach((item) => {
      item.addEventListener('click', () => {
        const key = item.dataset.key as NavKey | undefined;
        if (!key) return;
        this.closeAll();
        // Defer slightly so drawer animation starts before dialog opens
        setTimeout(() => opts.onNavAction(key), 120);
      });
    });
  }

  private updateStats(): void {
    const el = this.root;
    if (!el) return;

    const planets = this.mmoState.getPlayerPlanets();
    const stations = this.mmoState.getPlayerStations();
    const ships = this.mmoState.getPlayerUnits();
    const stationCount = stations.length + planets.length;
    const credits = this.mmoState.getResources();
    const explored = this.mmoState.getExploredHexes().size;

    const set = (id: string, text: string): void => {
      const n = el.querySelector('#' + id);
      if (n) n.textContent = text;
    };

    // Top bar — lean layout (turn + $ISD only; ships/stations live in Stats sheet)
    set('m-tick', `Turn ${this.mmoState.getTick()}`);
    set('m-isd', credits.toLocaleString('en-US'));

    // Fleet sheet (if open)
    if (this.fleetOpen) {
      set('f-stations', stationCount.toString());
      set('f-ships', ships.length.toString());
      set('f-explored', explored.toString());
      set('f-isd', credits.toLocaleString('en-US'));
      const activeTab = el.querySelector<HTMLElement>('.m-fleet-tab.active')?.dataset.tab ?? 'ships';
      this.renderFleetList(activeTab);
    }

    // Stats sheet (if open)
    if (this.statsOpen) {
      set('s-isd', credits.toLocaleString('en-US'));
      set('s-ships', ships.length.toString());
      set('s-stations', stations.length.toString());
      set('s-planets', planets.length.toString());
      set('s-explored', explored.toString());
      set('s-tick', this.mmoState.getTick().toString());
      const alliance = this.mmoState.getAlliance();
      set('s-alliance', alliance?.name ?? '—');
      set('s-territories', this.mmoState.getTerritories().length.toString());
    }
  }

  // ── Open/close helpers ────────────────────────────────────────
  private openFleet(): void {
    this.closeAll();
    this.fleetOpen = true;
    this.root.querySelector('#fleet-sheet')?.classList.add('open');
    this.root.querySelector('#m-backdrop')?.classList.add('open');
    this.updateStats();
    this.renderFleetList('ships');
    this.resetFleetBadge();
  }

  private openStats(): void {
    this.closeAll();
    this.statsOpen = true;
    this.root.querySelector('#stats-sheet')?.classList.add('open');
    this.root.querySelector('#m-backdrop')?.classList.add('open');
    this.updateStats();
  }

  private openNav(): void {
    this.closeAll();
    this.root.querySelector('#nav-drawer')?.classList.add('open');
    this.root.querySelector('#m-backdrop')?.classList.add('open');
  }

  private closeAll(): void {
    this.fleetOpen = false;
    this.statsOpen = false;
    this.root.querySelector('#fleet-sheet')?.classList.remove('open');
    this.root.querySelector('#stats-sheet')?.classList.remove('open');
    this.root.querySelector('#nav-drawer')?.classList.remove('open');
    this.root.querySelector('#m-backdrop')?.classList.remove('open');
  }

  // ── Fleet list rendering ──────────────────────────────────────
  private renderFleetList(tab: string): void {
    const list = this.root.querySelector<HTMLElement>('#fleet-list');
    if (!list) return;

    list.innerHTML = '';

    if (tab === 'ships') {
      const ships = this.mmoState.getPlayerUnits();
      if (ships.length === 0) {
        list.innerHTML = '<div class="m-fleet-empty">No ships yet — tap BUY SHIP to build your fleet.</div>';
        return;
      }
      ships.forEach((unit) => {
        const row = document.createElement('div');
        row.className = 'm-fleet-row';
        const name = unit.customName || this.unitDisplayName(unit.type);
        const hpPct = unit.maxHp > 0 ? unit.currentHp / unit.maxHp : 1;
        const hpCls = hpPct < 0.4 ? 'low' : '';
        const locationSuffix = unit.dockedAtPlanetId
          ? 'Docked'
          : `(${unit.position.q}, ${unit.position.r})`;
        row.innerHTML = `
          <div class="m-fleet-row-left">
            <span class="m-fleet-row-name">${escapeHtml(name)}</span>
            <span class="m-fleet-row-sub">${escapeHtml(unit.type)} &middot; ${locationSuffix}</span>
          </div>
          <span class="m-fleet-row-hp ${hpCls}">${unit.currentHp}/${unit.maxHp} HP</span>
        `;
        row.addEventListener('click', () => {
          if (unit.dockedAtPlanetId) {
            EventBus.emit('fleet-select-docked-unit', {
              unitId: unit.id,
              dockedAtId: unit.dockedAtPlanetId,
            });
          } else {
            EventBus.emit('fleet-select-unit', { unitId: unit.id });
          }
          this.closeAll();
        });
        list.appendChild(row);
      });
    } else {
      const planets = this.mmoState.getPlayerPlanets();
      const stations = this.mmoState.getPlayerStations();
      if (planets.length === 0 && stations.length === 0) {
        list.innerHTML = '<div class="m-fleet-empty">No owned stations or planets yet.</div>';
        return;
      }
      planets.forEach((p) => {
        const row = document.createElement('div');
        row.className = 'm-fleet-row';
        row.innerHTML = `
          <div class="m-fleet-row-left">
            <span class="m-fleet-row-name">${escapeHtml(p.name)}</span>
            <span class="m-fleet-row-sub">Planet &middot; (${p.position.q}, ${p.position.r})</span>
          </div>
          <span class="m-fleet-row-hp">${p.currentHp}/${p.maxHp}</span>
        `;
        row.addEventListener('click', () => {
          EventBus.emit('fleet-select-station', { stationId: p.id });
          this.closeAll();
        });
        list.appendChild(row);
      });
      stations.forEach((s) => {
        const row = document.createElement('div');
        row.className = 'm-fleet-row';
        row.innerHTML = `
          <div class="m-fleet-row-left">
            <span class="m-fleet-row-name">${escapeHtml(this.stationDisplayName(s.stationType))}</span>
            <span class="m-fleet-row-sub">${escapeHtml(s.status)} &middot; (${s.position.q}, ${s.position.r})</span>
          </div>
          <span class="m-fleet-row-hp">${s.currentHp}/${s.maxHp}</span>
        `;
        row.addEventListener('click', () => {
          EventBus.emit('fleet-select-station', { stationId: s.id });
          this.closeAll();
        });
        list.appendChild(row);
      });
    }
  }

  private unitDisplayName(type: string): string {
    const cleaned = type.replace(/_/g, ' ').toLowerCase();
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
  }

  private stationDisplayName(type: string): string {
    const cleaned = type.replace(/_/g, ' ').toLowerCase();
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1) + ' Station';
  }

  // The mobile CommandDock now slides in from the LEFT, so the action rail is
  // never covered during normal selection. The `.lifted` CSS class remains in
  // the stylesheet in case a future bottom-anchored layout returns.

  // ── Chat unread badge ──────────────────────────────────────
  private incrementChatUnread(): void {
    this.chatUnread++;
    this.updateChatBadge();
  }

  private resetChatUnread(): void {
    this.chatUnread = 0;
    this.updateChatBadge();
  }

  private updateChatBadge(): void {
    const badge = this.root.querySelector<HTMLElement>('#chat-badge');
    if (!badge) return;
    if (this.chatUnread > 0) {
      badge.textContent = this.chatUnread > 9 ? '9+' : String(this.chatUnread);
      badge.classList.add('visible');
    } else {
      badge.classList.remove('visible');
    }
  }

  // ── Fleet badge (e.g. new ship built, offline recap) ─────────
  public setFleetBadge(count: number): void {
    const badge = this.root.querySelector<HTMLElement>('#fleet-badge');
    if (!badge) return;
    if (count > 0) {
      badge.textContent = count > 9 ? '9+' : String(count);
      badge.classList.add('visible');
    } else {
      badge.classList.remove('visible');
    }
  }

  private resetFleetBadge(): void {
    this.setFleetBadge(0);
  }

  public destroy(): void {
    EventBus.off('mmo-tick-update', this.onTickBound);
    EventBus.off('mmo-chat-message', this.onChatMessageBound);
    EventBus.off('chat-message', this.onChatMessageBound);
    EventBus.off('mmo-command-dock-shown', this.onDockShownBound);
    EventBus.off('mmo-command-dock-hidden', this.onDockHiddenBound);
    EventBus.off('camera-follow-changed', this.onFollowChangedBound);
    this.domElement.destroy();
  }
}

// ── Module-scope helpers ──────────────────────────────────────
function renderNavGroups(): string {
  return NAV_GROUPS.map((g) => `
    <div class="m-nav-group">
      <div class="m-nav-group-title">${escapeHtml(g.title)}</div>
      <div class="m-nav-grid">
        ${g.items.map((item) => `
          <button class="m-nav-item" data-key="${escapeHtml(item.key)}">
            <span class="m-nav-item-icon">${item.icon}</span>
            <span>${escapeHtml(item.label)}</span>
          </button>
        `).join('')}
      </div>
    </div>
  `).join('');
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c] ?? c));
}
